
<script setup lang="ts">

import {IonButton, IonIcon, IonItem, IonLabel, IonList, IonListHeader, IonRadio} from '@ionic/vue'
import {computed, PropType, ref, Ref, watch} from "vue";
import {
    getEmployeeJobsites,
    getEmployeeJobsitesWithFlags,
    includesListJobsite,
    indexOfJobsiteInList
} from "@/helper/jobsite"
import {informationCircleOutline} from "ionicons/icons"
import {generateCalendarMonth} from "@/helper/calendar/calendarMonth"
import {AutopilotJobsite, Jobsite, JobsiteWithEmployeeFlagsFragment} from "@/graphql/generated/graphql"
import {DataLoader} from "@/helper/dataLoader"
import {
    addJobsiteToAutopilot,
    AutopilotPreferenceObject, fetchAutopilotPreferenceJobsites,
    fetchAutopilotPreferenceObject,
    removeJobsiteFromAutopilot
} from "@/helper/autopilotPreference"
import JobsiteSearchModal from "@/views/components/JobsiteSearchModal.vue"
import {JobsiteCategory, kapitelDateString} from '@/graphql/kapitelTypes';
import {getIconForJobsiteCategory, getLabelForJobsiteCategory, sortedJobsiteCategories} from '@/helper/jobsiteCategory';
import Modal from '@/components/Modal.vue'
import date from "@/helper/datetime/date";

import InlineSVG from '@/components/InlineSVG.vue';
import Skeleton from "@/components/Skeleton.vue";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {KapitelIcon} from "@/helper/icon";
import JobsiteListItem from "@/views/Settings/JobsiteListItem.vue";


/**
 * props & models
 */
const showMe = defineModel({type: Boolean})

const props = defineProps({
    monthId: {
        type: String as PropType<kapitelDateString>,
        required: true
    }
});


/**
 * fetched state
 */
const favoriteJobsites : Ref<Array<JobsiteWithEmployeeFlagsFragment>> = ref([]);
const secondChoiceJobsites : Ref<Array<JobsiteWithEmployeeFlagsFragment>> = ref([]);
const bookedJobsites : Ref<Array<JobsiteWithEmployeeFlagsFragment>> = ref([]);
const loadJobsites = async () => {
    const {favorite,secondChoice, booked} = await getEmployeeJobsitesWithFlags()

    favoriteJobsites.value = favorite
    favoriteJobsites.value.forEach(jobsite => listNewJobsite(jobsite, false, true))

    secondChoiceJobsites.value = secondChoice
    secondChoiceJobsites.value.forEach(jobsite => listNewJobsite(jobsite, false, true))

    bookedJobsites.value = booked
    bookedJobsites.value.forEach(jobsite => listNewJobsite(jobsite, false, true))
}


const loadAutopilotPreferenceJobsites = async () => {

    const jobsites = await fetchAutopilotPreferenceJobsites(calendarMonth.value)
    if (jobsites) {
        jobsites.forEach(jobsite => listNewJobsite(jobsite, true, true))
    }
}

const dataLoader = new DataLoader([loadJobsites,loadAutopilotPreferenceJobsites])


/**
 * local state & actions
 */

const jobsiteDetailModalData: Ref<number|undefined> = ref(undefined)
const jobsiteAddModalIsOpen = ref(false)

const listedJobsites : Ref<Array<JobsiteWithEmployeeFlagsFragment>> = ref([])
const selectedJobsites : Ref<Array<JobsiteWithEmployeeFlagsFragment>> = ref([])

const resetLocalState = () => {
    jobsiteDetailModalData.value = undefined
    jobsiteAddModalIsOpen.value = false

    listedJobsites.value = []
    selectedJobsites.value = []
}

const listNewJobsite = (jobsite: JobsiteWithEmployeeFlagsFragment, selectedState = true, silent = false) => {
    if (!includesListJobsite(listedJobsites.value, jobsite)) {
        listedJobsites.value.push(jobsite)
    }
    if (selectedState) {
        selectJobsite(jobsite, true, silent)
    }
}
const isSelected = (jobsite: JobsiteWithEmployeeFlagsFragment) => includesListJobsite(selectedJobsites.value, jobsite)
const selectJobsite = (jobsite: JobsiteWithEmployeeFlagsFragment, state = true, silent = false) => {
    const arr = selectedJobsites.value
    const selected =  includesListJobsite(arr, jobsite)
    if (state && !selected) {
        arr.push(jobsite)
        if (!silent) {
            addJobsiteToAutopilot(calendarMonth.value, jobsite)
        }
    } else if (!state && selected) {
        arr.splice(indexOfJobsiteInList(arr, jobsite),1)
        if (!silent) {
            removeJobsiteFromAutopilot(calendarMonth.value, jobsite)
        }
    }
}



/**
 * computed
 */

const calendarMonth = computed(() => generateCalendarMonth(props.monthId))

const getJobsiteCategory = (jobsite: Jobsite) : JobsiteCategory => {
    switch (true) {
        case jobsite.isFavorite: return JobsiteCategory.Favorite
        case jobsite.isAlternative: return JobsiteCategory.Alternative
        case jobsite.isBlacklist: return JobsiteCategory.Booked
        default: return JobsiteCategory.Other
    }
}
const listedJobsiteSortedRenderSets = computed(() => {
    return listedJobsites.value
        .map(jobsite => ({
            jobsite,
            label: jobsite.name || jobsite.abbreviation || '(fehlt)',
            clientLabel: jobsite.client?.abbreviation,
            houseLabel: jobsite.house?.abbreviation,
            additionalImportantLabel: jobsite.additionalAmenImportantAbbreviations ? jobsite.additionalAmenImportantAbbreviations:[],
            category: getJobsiteCategory(jobsite),
            disabledEffectively: jobsite.disabledEffectively,
        }))
        .sort((a, b) =>
            a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'})
        )
})
const jobsiteCategoriesRenderSets = computed(() => {
    return sortedJobsiteCategories
        .map((category) => { 
            const jobsiteRenderSets = listedJobsiteSortedRenderSets.value.filter(jobsiteRS => jobsiteRS.category === category)            
            return {
                category,
                label: getLabelForJobsiteCategory(category),
                icon: getIconForJobsiteCategory(category),
                jobsiteRenderSets,
                getSelectedJobsites() { return jobsiteRenderSets.map(jobsiteRS => jobsiteRS.jobsite).filter(isSelected) },
                getJobsiteCountLabel() {
                    const selectedCount = this.getSelectedJobsites().length
                    const totalCount = this.jobsiteRenderSets.length

                    return totalCount === selectedCount ? totalCount : (selectedCount + '/' + totalCount)
                }                                
            }
        })
        .filter(categoryRS => categoryRS.jobsiteRenderSets.length > 0)
})


/**
 * setup
 */
const reinit = () => {
    if (!showMe.value) {
        return
    }
    resetLocalState()
    dataLoader.load()
}
watch (showMe, reinit)
watch(() => props.monthId, reinit)
reinit()

</script>


<template>
<Modal v-model="showMe" :title="'Stationsauswahl ' + date.formatKapitelDate(props.monthId, 'MMMM')">
    <template v-slot:default>
    <Skeleton v-if="dataLoader.pending.value" :items="['title','block','block','block','margin','title','block','block']"></Skeleton>
    <ion-list  v-if="!dataLoader.pending.value" >
        <template v-for="categoryRS in jobsiteCategoriesRenderSets" :key="categoryRS.category">
            <ion-list-header>
                {{ categoryRS.label }}
                <div
                    class="icon"
                    v-if="categoryRS.icon"
                >
                    <InlineSVG
                        :src="categoryRS.icon"
                    />
                </div>
                ({{ categoryRS.getJobsiteCountLabel() }})
            </ion-list-header>

            <JobsiteListItem
                v-for="jobsiteRS in categoryRS.jobsiteRenderSets"

                :jobsite=jobsiteRS.jobsite
                selectable
                :selected=isSelected(jobsiteRS.jobsite)

                @info="jobsiteDetailModalData=jobsiteRS.jobsite.id"
                @click="selectJobsite(jobsiteRS.jobsite, !isSelected(jobsiteRS.jobsite))"
            />
        </template>
    </ion-list>

    <JobsiteSearchModal
        v-model="jobsiteAddModalIsOpen"
        :already-selected-jobsites="selectedJobsites"
        @select="jobsite => listNewJobsite(jobsite, true)"
    />

    <JobsiteDetails v-model="jobsiteDetailModalData" />
    </template>
    <template v-slot:actions>
        <ion-button
            color="light"
            expand="block"
            @click="jobsiteAddModalIsOpen = true"
        >
            Zusätzliche Station hinzufügen
        </ion-button>
        <ion-button
            color="primary"
            expand="block"
            @click="showMe = false"
        >
            Ok
        </ion-button>
    </template>
</Modal>
</template>


<style scoped lang = "scss">
.icon {
    width: 1em;
    line-height: 0.75em;
    margin: 0 0.5em 0 0.2em;
    color:var(--ion-color-primary)
}
</style>
